import { useCallback, useEffect, useRef, useState } from 'react';
import { useFluxHls } from '../lecture/useFluxHls';
import { urlDuDirect } from '../api/client';
import Participants from '../ui/Participants';
import { useParticipants } from '../discord/useParticipants';

/**
 * Le direct : ce qu'on regarde quand quelqu'un émet.
 *
 * Beaucoup plus dépouillé que `Lecteur`, et c'est voulu. Il n'y a personne aux
 * commandes, donc rien à afficher sur qui commande. Il n'y a pas de position,
 * donc rien à recaler ni à annoncer. Il n'y a pas de fin, donc pas de retour à
 * la bibliothèque à proposer — tant que ça émet, il n'y a rien d'autre à voir.
 *
 * Ce qui reste : une image, qui regarde avec nous, et de quoi passer en plein
 * écran. C'est tout ce que demande le fait de regarder un match ensemble.
 */

const DELAI_REPOS = 3000;

export default function Direct({ direct }) {
  const url = urlDuDirect(direct.playlist);
  const { proprietes, videoRef, erreur, recoit, rejoindreLaPointe } =
    useFluxHls({ url, actif: true });

  const participants = useParticipants();

  const sceneRef = useRef(null);
  const minuteurRef = useRef(null);
  const [barreVisible, setBarreVisible] = useState(true);
  const [pleinEcran, setPleinEcran] = useState(false);

  // Le navigateur refuse de lancer une lecture sonore sans intervention
  // humaine. Plutôt que de démarrer en sourdine — ce qui donne un match muet
  // que personne ne pense à réactiver —, on demande le geste franchement.
  const [aRejoint, setARejoint] = useState(false);

  /* ── Barre qui s'efface ───────────────────────────────────────────── */

  const reveiller = useCallback(() => {
    setBarreVisible(true);
    clearTimeout(minuteurRef.current);

    if (aRejoint) {
      minuteurRef.current = setTimeout(() => setBarreVisible(false), DELAI_REPOS);
    }
  }, [aRejoint]);

  useEffect(() => {
    reveiller();
    return () => clearTimeout(minuteurRef.current);
  }, [reveiller]);

  /* ── Plein écran ──────────────────────────────────────────────────── */

  const pleinEcranPossible = typeof document !== 'undefined' && document.fullscreenEnabled;

  useEffect(() => {
    const surChangement = () => setPleinEcran(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', surChangement);
    return () => document.removeEventListener('fullscreenchange', surChangement);
  }, []);

  const basculerPleinEcran = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else sceneRef.current?.requestFullscreen?.().catch(() => {});
  };

  /* ── Rendu ────────────────────────────────────────────────────────── */

  const rejoindre = () => {
    setARejoint(true);
    // On entre par la pointe, jamais par le début du tampon : rejoindre un
    // direct avec dix secondes de retard sur les autres n'aurait aucun sens.
    rejoindreLaPointe();
    videoRef.current?.play().catch(() => {});
  };

  return (
    <div className="scene" ref={sceneRef} onPointerMove={reveiller} onPointerDown={reveiller}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video className="video-plein" {...proprietes} />

      <header className={`scene-barre${barreVisible ? '' : ' effacee'}`}>
        <div className="scene-titres">
          <b>{direct.title}</b>
          <span>
            {erreur
              ? 'Signal perdu'
              : recoit
                ? '🔴 En direct'
                : 'Connexion au direct…'}
          </span>
        </div>

        <Participants participants={participants} compact />

        {pleinEcranPossible && (
          <button
            type="button"
            className="rond-bouton"
            onClick={basculerPleinEcran}
            aria-label={pleinEcran ? 'Quitter le plein écran' : 'Plein écran'}
          >
            {pleinEcran ? '⤡' : '⤢'}
          </button>
        )}
      </header>

      {!aRejoint && (
        <div className="voile">
          <p className="voile-titre">{direct.title}</p>
          <p className="discret">
            {direct.created_by_name
              ? `${direct.created_by_name} diffuse en direct.`
              : 'Une diffusion est en cours.'}
          </p>
          <button type="button" onClick={rejoindre}>Regarder</button>
        </div>
      )}

      {/* Le signal peut revenir : l'encodeur redémarre, le réseau hoquette. On
          le dit sans renvoyer personne ailleurs, puisque la reprise se fait
          toute seule dès que les segments recommencent à arriver. */}
      {aRejoint && erreur && (
        <div className="bandeau-bas">
          <span>{erreur}</span>
          <button type="button" onClick={() => window.location.reload()}>Réessayer</button>
        </div>
      )}
    </div>
  );
}
